import AddIcon from '@mui/icons-material/Add'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined'
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, TextField } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import React from 'react'
import { Link } from 'react-router-dom'
import { filterSx } from '../../../components/filter-fields'
import GrantContent from '../../../components/GrantContent'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import * as Permisos from '../../../utils/Permisos'
import { formatCount, PIPELINE_PATH, PROSPECTS_PATH, RamoMark } from '../common/opportunity-common'
import OpportunityDrawer from '../drawer/opportunity-drawer'
import { useOpportunityBoardModel } from './opportunity-board-model'

const SKELETON_COLUMNS = [1, 2, 3, 4]
const dateSx = { ...filterSx, width: { xs: '100%', sm: 180 } }

const outlineBtnSx = {
    width: 'auto',
    minWidth: 'auto',
    flex: 'none',
    height: 40,
    padding: '0 16px',
    borderRadius: '12px',
    boxShadow: 'none',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: 1,
    backgroundColor: 'rgb(var(--k-surface))',
    color: 'rgb(var(--k-ink))',
    border: '1px solid rgb(var(--k-line))',
    '&:hover': {
        backgroundColor: 'rgb(var(--k-hover))',
        border: '1px solid rgb(var(--k-line))',
        boxShadow: 'none',
    },
} satisfies SxProps<Theme>

const dangerBtnSx: SxProps<Theme> = {
    ...outlineBtnSx,
    backgroundColor: 'rgb(var(--k-danger))',
    color: '#fff',
    border: 0,
    '&:hover': {
        backgroundColor: 'rgb(var(--k-danger))',
        filter: 'brightness(1.08)',
        boxShadow: 'none',
    },
}

const DropLine = () => <div className="h-1 shrink-0 rounded-full bg-k-primary" aria-hidden="true" />

const isAfterMidpoint = (event: React.DragEvent<HTMLElement>, axis: 'x' | 'y') => {
    const rect = event.currentTarget.getBoundingClientRect()
    return axis === 'y' ? event.clientY > rect.top + rect.height / 2 : event.clientX > rect.left + rect.width / 2
}

const OpportunityBoard = () => {
    const model = useOpportunityBoardModel()
    const { filters, loading, dnd, cardHint, columnHint } = model

    return (
        <KarteraAdminTemplate loading={false} padded={false} title="Prospección">
            <OpportunityDrawer
                open={Boolean(model.drawer)}
                cardId={model.drawer?.cardId ?? null}
                columnId={model.drawer?.columnId ?? null}
                columns={model.columnOptions}
                onClose={model.closeDrawer}
                onSaved={model.cardSaved}
                onDelete={model.askDeleteCard}
            />

            <div className="flex min-h-0 flex-1 flex-col gap-[18px] bg-k-bg px-[clamp(16px,2.5vw,32px)] pb-10 pt-6">
                {loading && !model.initialLoadDone && (
                    <div className="flex min-h-0 flex-1 flex-col gap-[18px]">
                        <div>
                            <div className="mb-2 h-7 w-36 animate-pulse rounded bg-k-line md:h-8 md:w-44" />
                            <div className="hidden h-4 w-64 animate-pulse rounded bg-k-line md:block" />
                        </div>
                        <div className="h-[66px] animate-pulse rounded-[18px] bg-k-line" />
                        <div className="flex gap-4 overflow-hidden">
                            {SKELETON_COLUMNS.map((column) => (
                                <div key={column} className="flex w-[300px] shrink-0 flex-col gap-3 rounded-[18px] border border-k-line bg-k-surface p-3">
                                    <div className="h-5 w-32 animate-pulse rounded bg-k-line" />
                                    <div className="h-4 w-20 animate-pulse rounded bg-k-line" />
                                    {[1, 2, 3].map((card) => <div key={card} className="h-24 animate-pulse rounded-xl bg-k-line" />)}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {model.error && !loading && (
                    <div className="mb-6 rounded-lg border border-k-danger/30 bg-k-danger-soft p-4">
                        <div className="flex items-center">
                            <ErrorOutlineIcon className="mr-2 text-k-danger" style={{ fontSize: 20 }} />
                            <p className="text-sm text-k-danger-ink">{model.error}</p>
                        </div>
                        <Button variant="contained" onClick={model.reload} className="mt-3 !bg-k-danger !text-white">Reintentar</Button>
                    </div>
                )}

                {model.initialLoadDone && !model.error && (
                    <div className="flex min-h-0 flex-1 flex-col gap-[18px]">
                        <header className="flex shrink-0 flex-wrap items-center justify-between gap-3.5 md:items-end">
                            <div className="flex flex-col gap-1">
                                <h1 className="m-0 text-xl font-extrabold tracking-[-0.03em] text-k-ink md:text-[28px]">Prospección</h1>
                                <p className="m-0 hidden text-sm text-k-muted md:block">
                                    Embudo de oportunidades · {formatCount(model.openCount)} abiertas{model.canWrite ? ' · arrastra las tarjetas para cambiarlas de etapa' : ''}
                                </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2.5">
                                <button
                                    type="button"
                                    aria-label="Actualizar"
                                    title="Actualizar"
                                    className="inline-flex h-11 w-11 items-center justify-center gap-2 rounded-xl border border-k-line bg-k-surface text-sm font-semibold text-k-ink hover:bg-k-hover md:w-auto md:px-4"
                                    onClick={model.reload}
                                >
                                    <RefreshIcon className={loading ? 'animate-spin' : ''} style={{ fontSize: 18 }} />
                                    <span className="sr-only md:not-sr-only">Actualizar</span>
                                </button>
                                <GrantContent permisos={[Permisos.PROSPECTOS_R]}>
                                    <Link
                                        to={PROSPECTS_PATH}
                                        state={{ backUrl: PIPELINE_PATH }}
                                        aria-label="Ver prospectos"
                                        title="Ver prospectos"
                                        className="inline-flex h-11 w-11 items-center justify-center gap-2 rounded-xl border border-k-line bg-k-surface text-sm font-semibold !text-k-ink no-underline hover:bg-k-hover md:w-auto md:px-4"
                                    >
                                        <PersonSearchOutlinedIcon style={{ fontSize: 18 }} />
                                        <span className="sr-only md:not-sr-only">Ver prospectos</span>
                                    </Link>
                                </GrantContent>
                            </div>
                        </header>

                        <section className="flex shrink-0 items-start gap-2.5 rounded-[18px] border border-k-line bg-k-surface px-4 py-3.5">
                            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2.5">
                                <TextField
                                    type="date" size="small" label="Creadas del" sx={dateSx}
                                    value={filters.startDate}
                                    error={model.rangeInvalid}
                                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: filters.endDate || undefined } }}
                                    onChange={(event) => model.changeStartDate(event.target.value)}
                                />
                                <TextField
                                    type="date" size="small" label="Creadas al" sx={dateSx}
                                    value={filters.endDate}
                                    error={model.rangeInvalid}
                                    helperText={model.rangeInvalid ? 'Rango inválido' : undefined}
                                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: filters.startDate || undefined } }}
                                    onChange={(event) => model.changeEndDate(event.target.value)}
                                />
                                <FormControlLabel
                                    sx={{ mx: 0, color: 'rgb(var(--k-ink))', '& .MuiFormControlLabel-label': { fontSize: 14 } }}
                                    control={(
                                        <Checkbox
                                            size="small"
                                            checked={filters.showClosed}
                                            sx={{ color: 'rgb(var(--k-muted))', '&.Mui-checked': { color: 'rgb(var(--k-primary))' } }}
                                            onChange={(event) => model.changeShowClosed(event.target.checked)}
                                        />
                                    )}
                                    label="Ver oportunidades cerradas"
                                />
                            </div>
                            <button
                                type="button"
                                title="Limpiar filtros"
                                aria-label="Limpiar filtros"
                                className="inline-flex h-10 w-10 shrink-0 items-center justify-center self-start rounded-xl bg-k-danger-soft text-k-danger-ink hover:brightness-95"
                                onClick={model.clearFilters}
                            >
                                <CloseIcon style={{ fontSize: 18 }} />
                            </button>
                        </section>

                        <div className={`flex min-h-[420px] flex-1 items-stretch gap-3 overflow-x-auto pb-2 ${loading ? 'opacity-60' : ''}`}>
                            {model.columns.map((column, columnIndex) => (
                                <React.Fragment key={column.id}>
                                    {columnHint === columnIndex && <div className="w-1 shrink-0 self-stretch rounded-full bg-k-primary" aria-hidden="true" />}
                                    <section
                                        aria-label={`Etapa ${column.nombre}`}
                                        className={`flex w-[300px] shrink-0 flex-col rounded-[18px] border bg-k-surface ${cardHint?.columnId === column.id ? 'border-k-primary' : 'border-k-line'} ${model.dragging?.type === 'column' && model.dragging.columnId === column.id ? 'opacity-50' : ''}`}
                                        onDragOver={(event) => {
                                            if (!model.dragging) return
                                            event.preventDefault()
                                            dnd.overColumn(column.id, columnIndex, isAfterMidpoint(event, 'x'))
                                        }}
                                        onDrop={(event) => {
                                            event.preventDefault()
                                            dnd.drop()
                                        }}
                                    >
                                        <div
                                            className={`flex flex-col gap-1 border-b border-k-line px-3.5 pb-3 pt-3 ${model.canWrite ? 'cursor-grab' : ''}`}
                                            draggable={model.canWrite}
                                            onDragStart={(event) => {
                                                event.dataTransfer.effectAllowed = 'move'
                                                event.dataTransfer.setData('text/plain', `column-${column.id}`)
                                                dnd.startColumn(column.id)
                                            }}
                                            onDragEnd={dnd.end}
                                        >
                                            <div className="flex items-center gap-1.5">
                                                {model.canWrite && <DragIndicatorIcon className="text-k-muted-2" style={{ fontSize: 16 }} />}
                                                <h2 className="m-0 min-w-0 flex-1 truncate text-sm font-extrabold text-k-ink" title={column.nombre}>{column.nombre}</h2>
                                                <span className="rounded-full bg-k-line px-2 py-0.5 text-xs font-bold tabular-nums text-k-ink-2">{column.count}</span>
                                                <GrantContent permisos={[Permisos.PROSPECCION_D]}>
                                                    <button type="button" aria-label={`Eliminar columna ${column.nombre}`} title="Eliminar columna" className="inline-flex h-8 w-8 items-center justify-center rounded-lg border-0 bg-transparent text-k-muted hover:bg-k-danger-soft hover:text-k-danger-ink" onClick={() => model.askDeleteColumn(column.id)}>
                                                        <DeleteOutlinedIcon style={{ fontSize: 16 }} />
                                                    </button>
                                                </GrantContent>
                                            </div>
                                            <span className="text-xs tabular-nums text-k-muted">{column.totales}</span>
                                        </div>

                                        <div className="flex min-h-[120px] flex-1 flex-col gap-2 overflow-y-auto p-2.5">
                                            <GrantContent permisos={[Permisos.PROSPECCION_W]}>
                                                <button
                                                    type="button"
                                                    className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-xl border border-dashed border-k-line-strong bg-transparent text-[13px] font-bold text-k-ink-2 hover:bg-k-hover"
                                                    onClick={() => model.newCard(column.id)}
                                                >
                                                    <AddIcon style={{ fontSize: 16 }} />Oportunidad
                                                </button>
                                            </GrantContent>

                                            {column.cards.length === 0 && cardHint?.columnId !== column.id && (
                                                <p className="m-0 px-2 py-6 text-center text-xs italic text-k-muted">Sin oportunidades en esta etapa.</p>
                                            )}

                                            {column.cards.map((card, cardIndex) => (
                                                <React.Fragment key={card.id}>
                                                    {cardHint?.columnId === column.id && cardHint.index === cardIndex && <DropLine />}
                                                    <article
                                                        draggable={model.canWrite}
                                                        tabIndex={0}
                                                        aria-label={`Oportunidad ${card.nombre}`}
                                                        className={`flex shrink-0 cursor-pointer flex-col gap-2 rounded-xl border border-k-line bg-k-bg px-3 py-2.5 transition-shadow hover:border-k-line-strong hover:shadow-k-pop ${model.dragging?.type === 'card' && model.dragging.cardId === card.id ? 'opacity-40' : ''}`}
                                                        onClick={() => model.openCard(card.id, column.id)}
                                                        onKeyDown={(event) => {
                                                            if (event.key === 'Enter') model.openCard(card.id, column.id)
                                                        }}
                                                        onDragStart={(event) => {
                                                            event.stopPropagation()
                                                            event.dataTransfer.effectAllowed = 'move'
                                                            event.dataTransfer.setData('text/plain', `card-${card.id}`)
                                                            dnd.startCard(card.id, column.id)
                                                        }}
                                                        onDragEnd={dnd.end}
                                                        onDragOver={(event) => {
                                                            if (model.dragging?.type !== 'card') return
                                                            event.preventDefault()
                                                            event.stopPropagation()
                                                            dnd.overCard(column.id, cardIndex + (isAfterMidpoint(event, 'y') ? 1 : 0))
                                                        }}
                                                    >
                                                        <div className="flex items-center gap-1.5 text-[13px] font-bold text-k-ink">
                                                            {card.moral
                                                                ? <BusinessOutlinedIcon className="shrink-0 text-k-muted" style={{ fontSize: 16 }} />
                                                                : <PersonOutlinedIcon className="shrink-0 text-k-muted" style={{ fontSize: 16 }} />}
                                                            <span className="min-w-0 truncate" title={card.prospecto}>{card.prospecto}</span>
                                                            {card.status && <span className={`ml-auto shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${card.status.className}`}>{card.status.label}</span>}
                                                        </div>
                                                        <span className="text-sm font-semibold text-k-ink-2">{card.nombre}</span>
                                                        {card.descripcion && <p className="m-0 line-clamp-2 text-xs text-k-muted">{card.descripcion}</p>}
                                                        <div className="flex items-center justify-between gap-2 border-t border-k-line pt-2 text-xs text-k-muted">
                                                            <span className="flex items-center gap-1.5" title={`${card.dias} en el embudo`}>
                                                                <RamoMark ramo={card.ramo} />
                                                                {card.dias}
                                                            </span>
                                                            <span className="font-extrabold tabular-nums text-k-ink">{card.prima}</span>
                                                        </div>
                                                        <span className="flex items-center gap-1 text-[11px] tabular-nums text-k-muted-2">
                                                            <ScheduleOutlinedIcon style={{ fontSize: 13 }} />{card.fecha}
                                                        </span>
                                                    </article>
                                                </React.Fragment>
                                            ))}
                                            {cardHint?.columnId === column.id && cardHint.index >= column.cards.length && <DropLine />}
                                        </div>
                                    </section>
                                </React.Fragment>
                            ))}
                            {columnHint != null && columnHint >= model.columns.length && <div className="w-1 shrink-0 self-stretch rounded-full bg-k-primary" aria-hidden="true" />}

                            <GrantContent permisos={[Permisos.PROSPECCION_W]}>
                                <div className="flex w-[300px] shrink-0 flex-col gap-2.5 self-start rounded-[18px] border border-dashed border-k-line-strong bg-k-surface p-3">
                                    {!model.addingColumn ? (
                                        <button type="button" className="inline-flex h-10 items-center justify-center gap-1.5 rounded-xl border-0 bg-transparent text-[13px] font-bold text-k-ink hover:bg-k-hover" onClick={model.startAddColumn}>
                                            <AddIcon style={{ fontSize: 16 }} />Agregar etapa
                                        </button>
                                    ) : (
                                        <>
                                            <TextField
                                                autoFocus
                                                size="small"
                                                label="Nombre de la etapa"
                                                value={model.columnName}
                                                sx={{ ...filterSx, width: '100%' }}
                                                onChange={(event) => model.changeColumnName(event.target.value)}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter') model.addColumn()
                                                    if (event.key === 'Escape') model.cancelAddColumn()
                                                }}
                                            />
                                            <div className="flex justify-end gap-2">
                                                <button type="button" className="h-9 rounded-[10px] border border-k-line bg-transparent px-3 text-[13px] font-semibold text-k-ink" onClick={model.cancelAddColumn}>Cancelar</button>
                                                <button type="button" disabled={!model.columnName.trim() || model.savingColumn} className="h-9 rounded-[10px] border-0 bg-k-primary px-3 text-[13px] font-bold text-white disabled:opacity-40" onClick={model.addColumn}>Agregar</button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </GrantContent>
                        </div>
                    </div>
                )}
            </div>

            <Dialog open={Boolean(model.confirmColumn)} onClose={model.cancelDeleteColumn} slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>Eliminar etapa</DialogTitle>
                <DialogContent>
                    <p className="m-0 text-sm text-k-ink-2">
                        ¿Desea eliminar la etapa <strong>{model.confirmColumn?.nombre}</strong>? Se perderán sus oportunidades y su actividad.
                    </p>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" sx={outlineBtnSx} onClick={model.cancelDeleteColumn}>Cancelar</Button>
                    <Button variant="contained" sx={dangerBtnSx} onClick={model.deleteColumn}>Eliminar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(model.confirmCard)} onClose={model.cancelDeleteCard} slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>Eliminar oportunidad</DialogTitle>
                <DialogContent>
                    <p className="m-0 text-sm text-k-ink-2">¿Desea eliminar la oportunidad <strong>{model.confirmCard?.nombre}</strong> y su actividad?</p>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" sx={outlineBtnSx} onClick={model.cancelDeleteCard}>Cancelar</Button>
                    <Button variant="contained" sx={dangerBtnSx} onClick={model.deleteCard}>Eliminar</Button>
                </DialogActions>
            </Dialog>

            {model.toast && (
                <div role="status" className="fixed bottom-6 left-6 z-[60] flex items-center gap-2.5 rounded-xl bg-[#1B1D29] px-4 py-3 text-sm text-white shadow-[0_12px_32px_rgba(10,12,20,0.3)]">
                    {model.toast}
                </div>
            )}
        </KarteraAdminTemplate>
    )
}

export default OpportunityBoard
